import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Evolution } from 'src/app/shared/models/evolution';
import { EvolutionAiAnalysis } from 'src/app/shared/models/evolutionAiAnalysis';
import { EvolutionsService } from 'src/app/core/services/evolutions.service';
import { AlertService } from 'src/app/core/services/alert.service';
import { ExperienceI18nService } from 'src/app/core/experience/experience-i18n.service';

export interface RateEvolutionDialogData {
    evolution: Evolution;
    analysis: EvolutionAiAnalysis;
}

@Component({
    selector: 'app-rate-evolution-dialog',
    templateUrl: './rate-evolution-dialog.component.html',
    styleUrls: ['./rate-evolution-dialog.component.scss'],
})
export class RateEvolutionDialogComponent implements OnInit {
    form: FormGroup;
    saving = false;
    /** Display stars 0–5 (half steps); persisted as ×2 → 0–10 */
    starValue = 0;

    constructor(
        private fb: FormBuilder,
        private evolutionsService: EvolutionsService,
        private alertService: AlertService,
        private experienceI18n: ExperienceI18nService,
        private dialogRef: MatDialogRef<RateEvolutionDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: RateEvolutionDialogData,
    ) {}

    ngOnInit(): void {
        const initial = this.data.evolution.auditorScore ?? this.data.analysis?.auditorScore;
        this.starValue = initial != null ? initial / 2 : 0;
        this.form = this.fb.group({
            auditorNotes: [this.data.evolution.auditorNotes ?? this.data.analysis?.auditorNotes ?? ''],
        });
    }

    get previewText(): string {
        const item = this.data.analysis?.analyses?.[0];
        return item?.text ?? this.data.evolution.text ?? '';
    }

    get titleDate(): string {
        const d = this.data.evolution.date;
        if (!d) {
            return '';
        }
        return new Date(d).toLocaleDateString('es-AR');
    }

    setStar(starIndex: number, event: MouseEvent): void {
        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        const half = event.clientX - rect.left < rect.width / 2;
        this.starValue = half ? starIndex - 0.5 : starIndex;
        if (this.starValue < 0) {
            this.starValue = 0;
        }
    }

    starIcon(starIndex: number): string {
        if (this.starValue >= starIndex) {
            return 'star';
        }
        if (this.starValue >= starIndex - 0.5) {
            return 'star_half';
        }
        return 'star_border';
    }

    get canSave(): boolean {
        return this.starValue > 0 && !this.saving;
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    save(): void {
        if (!this.canSave) {
            return;
        }
        this.saving = true;
        const auditorScore = this.starValue * 2;
        this.evolutionsService.patchAuditorRating(this.data.evolution.id, {
            auditorScore,
            auditorNotes: this.form.get('auditorNotes')?.value?.trim() || undefined,
        }).subscribe({
            next: (updated) => {
                this.dialogRef.close(updated);
            },
            error: () => {
                this.saving = false;
                this.alertService.openError(
                    this.experienceI18n.translate(
                        'modules.cases.evolutions.rate-evolution.save-error',
                        undefined,
                        'No se pudo guardar la calificación',
                    ),
                );
            },
        });
    }
}
